import { CloseButton, Flex, Text } from '@chakra-ui/react'
import React, { useMemo } from 'react'

import { textStyleKeys } from '@mangovoice/design-system/textStyles'

import {
  type ExtensionNameMap,
  formatExtensionLabel,
} from '../hooks/useExtensionNames'
import { useReportingI18n } from '../i18n'
import { Tooltip } from './ui/Tooltip'

interface ExtensionPillsProps {
  selectedExtensions: string[]
  extensionNameMap?: ExtensionNameMap
  onRemoveExtension: (ext: string) => void
  onClearAll: () => void
  maxVisiblePills?: number
}

const IndividualPill = ({
  label,
  onRemove,
}: {
  label: string
  onRemove: () => void
}) => (
  <Flex
    css={{
      height: '1.75rem',
      px: '0.75rem',
      py: '0.375rem',
      bg: 'brand.subtle',
      border: '1px solid',
      borderColor: 'brand.solid',
      borderRadius: '1rem',
      gap: '0.25rem',
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
    }}
  >
    <Text
      textStyle={textStyleKeys.Body2}
      css={{
        color: 'brand.solid',
        fontWeight: 400,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Text>
    <CloseButton
      onClick={onRemove}
      size="xs"
      css={{
        color: 'brand.solid',
        width: '1rem',
        height: '1rem',
        minW: '1rem',
        borderRadius: '0.125rem',
        _hover: {
          bg: 'brand.muted',
          color: 'brand.solid',
        },
      }}
    />
  </Flex>
)

const SummaryPill = ({
  tooltipContent,
  onClearAll,
  label,
}: {
  tooltipContent: string
  onClearAll: () => void
  label: string
}) => (
  <Tooltip
    content={tooltipContent}
    showArrow
    positioning={{ placement: 'top' }}
    contentProps={{
      bg: 'fg.subtle',
      color: 'brand.contrast',
      px: '0.75rem',
      py: '0.5rem',
      borderRadius: '0.25rem',
      fontSize: '0.875rem',
      maxW: '20rem',
    }}
  >
    <Flex
      css={{
        height: '1.75rem',
        px: '0.75rem',
        py: '0.375rem',
        bg: 'brand.subtle',
        border: '1px solid',
        borderColor: 'brand.solid',
        borderRadius: '1rem',
        gap: '0.25rem',
        display: 'flex',
        alignItems: 'center',
        cursor: 'default',
        flexShrink: 0,
      }}
    >
      <Text
        textStyle={textStyleKeys.Body2}
        css={{
          color: 'brand.solid',
          fontWeight: 400,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Text>
      <CloseButton
        onClick={onClearAll}
        size="xs"
        css={{
          color: 'brand.solid',
          width: '1rem',
          height: '1rem',
          minW: '1rem',
          borderRadius: '0.125rem',
          _hover: {
            bg: 'brand.muted',
            color: 'brand.solid',
          },
        }}
      />
    </Flex>
  </Tooltip>
)

export const ExtensionPills = ({
  selectedExtensions,
  extensionNameMap,
  onRemoveExtension,
  onClearAll,
  maxVisiblePills = 2,
}: ExtensionPillsProps) => {
  const { text } = useReportingI18n()
  const shouldShowSummaryPill = selectedExtensions.length > maxVisiblePills

  const summaryTooltipContent = useMemo(() => {
    const map = extensionNameMap ?? new Map()
    return selectedExtensions
      .map((ext) => formatExtensionLabel(ext, map))
      .join(', ')
  }, [selectedExtensions, extensionNameMap])

  if (selectedExtensions.length === 0) {
    return null
  }

  return (
    <>
      {shouldShowSummaryPill ? (
        <SummaryPill
          tooltipContent={summaryTooltipContent}
          onClearAll={onClearAll}
          label={text(
            'extensionPills.extensionsSelected',
            '{{count}} extensions selected',
            { count: selectedExtensions.length },
          )}
        />
      ) : (
        <>
          {selectedExtensions.map((ext) => (
            <IndividualPill
              key={ext}
              label={formatExtensionLabel(ext, extensionNameMap ?? new Map())}
              onRemove={() => onRemoveExtension(ext)}
            />
          ))}
        </>
      )}
    </>
  )
}
